// Rig metadata shared by the procedural art (art.ts) and the runtime
// (enemy.ts): texture keys, frame layout, anchors, poses and the attachment
// points derived from them. Pure math — no Phaser, no canvas — so the drawn
// frames and the runtime attachment points (gun pivots, sensors) always agree.
//
// Coordinates are pixels relative to the frame anchor (feet centre for ground
// units, body centre for flyers), right-facing, y down.

import type { EnemyId } from '../../core/types';

export interface Vec2 {
  x: number;
  y: number;
}

export type EnemyAnim = 'idle' | 'move' | 'windup' | 'attack' | 'stagger' | 'leap';
export const ENEMY_ANIMS: readonly EnemyAnim[] = ['idle', 'move', 'windup', 'attack', 'stagger', 'leap'];

export interface AnimSpec {
  frames: number;
  /** Playback rate for time-driven animations (move is distance-driven for ground units). */
  fps: number;
  loop: boolean;
}

/** Rotating arm + weapon sprite (trooper rifle, lancer lance). */
export interface GunRig {
  /** Frame size. */
  w: number;
  h: number;
  /** Shoulder pivot inside the frame. */
  pivotX: number;
  pivotY: number;
  /** Muzzle relative to the pivot (weapon pointing right). */
  muzzleX: number;
  muzzleY: number;
  /** Local aim limits in radians (negative = up). */
  minAngle: number;
  maxAngle: number;
  /** Relaxed local angle while not aiming. */
  restAngle: number;
}

/** Separate shield sprite (bulwark). */
export interface ShieldRig {
  key: string;
  w: number;
  h: number;
  /** Grip point inside the frame (sits in the bulwark's fist). */
  gripX: number;
  gripY: number;
  /** Energy plane extent relative to the grip (right-facing). */
  planeX0: number;
  planeX1: number;
  planeY0: number;
  planeY1: number;
}

export interface RigDef {
  id: EnemyId;
  /** Spritesheet texture key. */
  key: string;
  cellW: number;
  cellH: number;
  /** Anchor inside the cell. */
  anchorX: number;
  anchorY: number;
  /** Visual height of the standing figure (labels, bars, markers). */
  height: number;
  /** Visual width (flyers). */
  width: number;
  anims: Partial<Record<EnemyAnim, AnimSpec>>;
  /** Number of debris frames ('gib0'..). */
  gibs: number;
  /** Distance-driven walk cycle: px of travel per full move cycle. */
  strideCycle: number;
  gun?: GunRig;
  shield?: ShieldRig;
}

export const enemyTextureKey = (id: EnemyId): string => `enemy_${id}`;
/** Enemy sheets are baked at this scale; sprites are drawn at 1/ENEMY_ART_SCALE. */
export const ENEMY_ART_SCALE = 2;

/**
 * On-screen size per type: `view` scales the drawn figure (trooper / lancer ≈ a player mech,
 * bulwark clearly bigger), `body` the physics box. Bodies grow less than the art so they still
 * fit the corridors the player fits through (player body 40×88, tiles 48).
 */
export const ENEMY_SIZE: Readonly<Record<EnemyId, { view: number; body: number }>> = {
  trooper: { view: 1.85, body: 1.35 },
  lancer: { view: 1.8, body: 1.2 },
  bulwark: { view: 2.1, body: 1.25 },
  hornet: { view: 1.25, body: 1.3 },
};
export const frameName = (anim: EnemyAnim, i: number): string => `${anim}${i}`;
/** Elite outline mask frame for any frame name. */
export const outlineFrame = (name: string): string => `${name}_o`;
/** Phaser animation key (created by buildEnemyArt for previews / external use). */
export const enemyAnimKey = (id: EnemyId, anim: EnemyAnim): string => `enemy_${id}_${anim}`;

/** Shared FX textures generated by buildEnemyArt (white, tinted at runtime). */
export const ENEMY_FX = {
  aura: 'enemy_fx_aura',
  hex: 'enemy_fx_hex',
  star: 'enemy_fx_star',
  core: 'enemy_fx_core',
  bar: 'enemy_fx_bar',
  placeholder: 'enemy_placeholder',
} as const;

export const RIGS: Readonly<Record<EnemyId, RigDef>> = {
  trooper: {
    id: 'trooper',
    key: enemyTextureKey('trooper'),
    cellW: 96,
    cellH: 96,
    anchorX: 44,
    anchorY: 92,
    height: 70,
    width: 40,
    anims: {
      idle: { frames: 4, fps: 6, loop: true },
      move: { frames: 8, fps: 13, loop: true },
      windup: { frames: 3, fps: 8, loop: false },
      attack: { frames: 3, fps: 18, loop: false },
      stagger: { frames: 2, fps: 9, loop: true },
    },
    gibs: 3,
    strideCycle: 56,
    gun: { w: 96, h: 56, pivotX: 30, pivotY: 22, muzzleX: 45, muzzleY: 4.5, minAngle: -1.15, maxAngle: 0.95, restAngle: 0.42 },
  },
  hornet: {
    id: 'hornet',
    key: enemyTextureKey('hornet'),
    cellW: 128,
    cellH: 104,
    anchorX: 74,
    anchorY: 54,
    height: 40,
    width: 58,
    anims: {
      idle: { frames: 4, fps: 18, loop: true },
      move: { frames: 4, fps: 20, loop: true },
      windup: { frames: 3, fps: 8, loop: false },
      attack: { frames: 2, fps: 20, loop: true },
      stagger: { frames: 2, fps: 10, loop: true },
    },
    gibs: 3,
    strideCycle: 0,
  },
  lancer: {
    id: 'lancer',
    key: enemyTextureKey('lancer'),
    cellW: 96,
    cellH: 112,
    anchorX: 46,
    anchorY: 108,
    height: 81,
    width: 36,
    anims: {
      idle: { frames: 4, fps: 5, loop: true },
      move: { frames: 8, fps: 10, loop: true },
      windup: { frames: 3, fps: 6, loop: false },
      attack: { frames: 2, fps: 12, loop: false },
      stagger: { frames: 2, fps: 9, loop: true },
      leap: { frames: 2, fps: 12, loop: true },
    },
    gibs: 3,
    strideCycle: 68,
    gun: { w: 140, h: 56, pivotX: 36, pivotY: 24, muzzleX: 84, muzzleY: 1.5, minAngle: -1.25, maxAngle: 1.1, restAngle: 0.5 },
  },
  bulwark: {
    id: 'bulwark',
    key: enemyTextureKey('bulwark'),
    cellW: 128,
    cellH: 128,
    anchorX: 58,
    anchorY: 124,
    height: 94,
    width: 64,
    anims: {
      idle: { frames: 4, fps: 5, loop: true },
      move: { frames: 8, fps: 8, loop: true },
      windup: { frames: 3, fps: 6, loop: false },
      attack: { frames: 3, fps: 12, loop: false },
      stagger: { frames: 2, fps: 8, loop: true },
    },
    gibs: 3,
    strideCycle: 44,
    shield: { key: 'enemy_bulwark_shield', w: 64, h: 140, gripX: 14, gripY: 72, planeX0: 8, planeX1: 22, planeY0: -66, planeY1: 60 },
  },
};

// ───────────────────────────── Math helpers ─────────────────────────────

const TAU = Math.PI * 2;

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/**
 * Two-bone IK. Returns the middle joint for a chain hip → knee → end.
 * bend = 1 puts the knee clockwise of the hip→end line (behind for a right-facing
 * leg: digitigrade), bend = -1 in front (human knee).
 */
export function ik2(root: Vec2, end: Vec2, l1: number, l2: number, bend: 1 | -1): { mid: Vec2; end: Vec2 } {
  let dx = end.x - root.x;
  let dy = end.y - root.y;
  let d = Math.hypot(dx, dy);
  const max = l1 + l2 - 0.05;
  const min = Math.abs(l1 - l2) + 0.05;
  if (d < 1e-6) {
    dx = 0;
    dy = 1;
    d = 1;
  }
  const k = clamp(d, min, max) / d;
  dx *= k;
  dy *= k;
  d *= k;
  const a = Math.atan2(dy, dx);
  const off = Math.acos(clamp((l1 * l1 + d * d - l2 * l2) / (2 * l1 * d), -1, 1));
  const ka = a + bend * off;
  return {
    mid: { x: root.x + Math.cos(ka) * l1, y: root.y + Math.sin(ka) * l1 },
    end: { x: root.x + dx, y: root.y + dy },
  };
}

/** Foot position on a walk cycle: stance on the ground while moving back, lifted swing forward. */
function stepFoot(cx: number, ground: number, ph: number, stride: number, lift: number): Vec2 {
  return { x: cx + stride * Math.cos(ph), y: ground - lift * Math.max(0, -Math.sin(ph)) };
}

// ───────────────────────────── Poses ─────────────────────────────

export interface LegPose {
  hip: Vec2;
  knee: Vec2;
  ankle: Vec2;
  /** Foot rotation in radians (toe down positive). */
  foot: number;
}

export interface BipedPose {
  hip: Vec2;
  /** Torso lean (px the chest is shifted forward). */
  lean: number;
  head: Vec2;
  headTilt: number;
  /** Gun / arm pivot. */
  shoulder: Vec2;
  /** Bulwark shield grip (others: same as shoulder). */
  grip: Vec2;
  legF: LegPose;
  legR: LegPose;
  /** 0..1 emissive sensor intensity. */
  sensor: number;
  /** Type-specific 0..1 extra (antenna light, jets, vents). */
  extra: number;
}

export interface HornetPose {
  /** Body rotation (radians, positive = nose down). */
  tilt: number;
  /** Wing angles (radians, screen space; -PI = pointing straight back). */
  wingF: number;
  wingR: number;
  /** Stinger extension 0..1 and angle (0 = straight forward). */
  sting: number;
  stingAng: number;
  /** Abdomen curl 0..1. */
  curl: number;
  bob: number;
  sensor: number;
  /** Wing motion blur strength 0..1. */
  blur: number;
}

function leg(hip: Vec2, ankle: Vec2, l1: number, l2: number, bend: 1 | -1, foot = 0): LegPose {
  const r = ik2(hip, ankle, l1, l2, bend);
  return { hip, knee: r.mid, ankle: r.end, foot };
}

/** Humanoid soldier: bent human knees, forward-leaning torso. */
export function trooperPose(anim: EnemyAnim, i: number): BipedPose {
  const L1 = 16;
  const L2 = 17;
  let hx = -1;
  let hy = -35;
  let lean = 0;
  let sensor = 0.8;
  let extra = 0.3;
  let tilt = 0;
  let fa: Vec2 = { x: 5, y: -5 };
  let ra: Vec2 = { x: -7, y: -5 };
  let fFoot = 0;
  let rFoot = 0;
  switch (anim) {
    case 'idle': {
      hy += [0, 0.6, 1.1, 0.6][i % 4];
      sensor = [0.75, 0.9, 1, 0.9][i % 4];
      extra = i % 4 < 2 ? 1 : 0.15;
      break;
    }
    case 'move': {
      const ph = (i / 8) * TAU;
      hy += 0.8 - 1.6 * Math.abs(Math.sin(ph));
      lean = 3;
      fa = stepFoot(hx + 2, -5, ph, 13, 7);
      ra = stepFoot(hx + 2, -5, ph + Math.PI, 13, 7);
      fFoot = Math.max(0, -Math.sin(ph)) * 0.35;
      rFoot = Math.max(0, Math.sin(ph)) * 0.35;
      sensor = 0.85;
      extra = i % 4 < 2 ? 1 : 0.2;
      break;
    }
    case 'windup': {
      hy += [1.5, 3, 3.5][i % 3];
      lean = [1.5, 2.5, 3][i % 3];
      fa = { x: 10, y: -5 };
      ra = { x: -11, y: -5 };
      sensor = [0.9, 1, 1][i % 3];
      extra = 1;
      break;
    }
    case 'attack': {
      hy += 3;
      lean = [-2.5, -1.2, 0][i % 3];
      hx += [-1.5, -0.7, 0][i % 3];
      fa = { x: 10, y: -5 };
      ra = { x: -11, y: -5 };
      sensor = 1;
      extra = 1;
      break;
    }
    case 'stagger': {
      hy += [1, 2][i % 2];
      hx -= 2;
      lean = [-4.5, -3.5][i % 2];
      tilt = [-0.28, -0.2][i % 2];
      fa = { x: 8, y: -9 };
      ra = { x: -8, y: -5 };
      fFoot = 0.4;
      sensor = [0.25, 0.5][i % 2];
      extra = 0;
      break;
    }
    default:
      break;
  }
  const hip = { x: hx, y: hy };
  return {
    hip,
    lean,
    head: { x: hx + 8 + lean, y: hy - 28 },
    headTilt: tilt,
    shoulder: { x: hx + 4 + lean * 0.9, y: hy - 19 },
    grip: { x: hx + 4 + lean * 0.9, y: hy - 19 },
    legF: leg(hip, fa, L1, L2, -1, fFoot),
    legR: leg({ x: hx - 2, y: hy }, ra, L1, L2, -1, rFoot),
    sensor,
    extra,
  };
}

/** Tall stilt-legged sniper: digitigrade legs, jet pack. */
export function lancerPose(anim: EnemyAnim, i: number): BipedPose {
  const L1 = 19;
  const L2 = 20;
  let hx = 0;
  let hy = -40;
  let lean = 0;
  let sensor = 0.8;
  let extra = 0;
  let tilt = 0;
  let fa: Vec2 = { x: 6, y: -4 };
  let ra: Vec2 = { x: -6, y: -4 };
  let fFoot = 0;
  let rFoot = 0;
  switch (anim) {
    case 'idle': {
      hy += [0, 0.5, 1, 0.5][i % 4];
      lean = [0, 0.3, 0.6, 0.3][i % 4];
      sensor = [0.7, 0.85, 1, 0.85][i % 4];
      break;
    }
    case 'move': {
      const ph = (i / 8) * TAU;
      hy += 1 - 2 * Math.abs(Math.sin(ph));
      lean = 2;
      fa = stepFoot(hx + 2, -4, ph, 16, 9);
      ra = stepFoot(hx + 2, -4, ph + Math.PI, 16, 9);
      fFoot = Math.max(0, -Math.sin(ph)) * 0.4;
      rFoot = Math.max(0, Math.sin(ph)) * 0.4;
      break;
    }
    case 'windup': {
      hy += [2, 4, 5][i % 3];
      lean = [1, 2, 2.5][i % 3];
      fa = { x: 13, y: -4 };
      ra = { x: -12, y: -4 };
      sensor = 1;
      extra = 0;
      break;
    }
    case 'attack': {
      hy += 4;
      lean = [-3.5, -1.5][i % 2];
      hx += [-2, -1][i % 2];
      fa = { x: 13, y: -4 };
      ra = { x: -12, y: -4 };
      sensor = 1;
      break;
    }
    case 'stagger': {
      hy += [1, 2][i % 2];
      hx -= 2;
      lean = [-4, -3][i % 2];
      tilt = [-0.3, -0.22][i % 2];
      fa = { x: 10, y: -10 };
      fFoot = 0.5;
      sensor = [0.25, 0.5][i % 2];
      break;
    }
    case 'leap': {
      hy += -2;
      lean = 1;
      fa = { x: 8, y: -14 + i * 2 };
      ra = { x: -6, y: -12 + i * 2 };
      fFoot = 0.6;
      rFoot = 0.7;
      extra = 1;
      sensor = 1;
      break;
    }
  }
  const hip = { x: hx, y: hy };
  return {
    hip,
    lean,
    head: { x: hx + 5 + lean, y: hy - 33 },
    headTilt: tilt,
    shoulder: { x: hx + 3 + lean * 0.9, y: hy - 21 },
    grip: { x: hx + 3 + lean * 0.9, y: hy - 21 },
    legF: leg(hip, fa, L1, L2, 1, fFoot),
    legR: leg({ x: hx - 2, y: hy }, ra, L1, L2, 1, rFoot),
    sensor,
    extra,
  };
}

/** Hunched heavy: thick human-kneed legs, shield arm forward. */
export function bulwarkPose(anim: EnemyAnim, i: number): BipedPose {
  const L1 = 16;
  const L2 = 17;
  let hx = -4;
  let hy = -31;
  let lean = 0;
  let sensor = 0.8;
  let extra = 0.4;
  let tilt = 0;
  let gripDx = 0;
  let fa: Vec2 = { x: 7, y: -6 };
  let ra: Vec2 = { x: -13, y: -6 };
  let fFoot = 0;
  let rFoot = 0;
  switch (anim) {
    case 'idle': {
      hy += [0, 0.6, 1.2, 0.6][i % 4];
      extra = [0.5, 0.7, 0.9, 0.7][i % 4];
      sensor = [0.75, 0.85, 1, 0.85][i % 4];
      break;
    }
    case 'move': {
      const ph = (i / 8) * TAU;
      hy += 1.2 - 2.4 * Math.abs(Math.sin(ph));
      lean = 2;
      fa = stepFoot(hx + 1, -6, ph, 10, 6);
      ra = stepFoot(hx - 3, -6, ph + Math.PI, 10, 6);
      fFoot = Math.max(0, -Math.sin(ph)) * 0.25;
      rFoot = Math.max(0, Math.sin(ph)) * 0.25;
      extra = 0.6;
      break;
    }
    case 'windup': {
      hy += [3, 6, 7][i % 3];
      lean = [2, 4, 5][i % 3];
      gripDx = [-2, -4, -5][i % 3];
      fa = { x: 12, y: -6 };
      ra = { x: -18, y: -6 };
      sensor = 1;
      extra = 1;
      break;
    }
    case 'attack': {
      hy += [3, 2, 2][i % 3];
      hx += [3, 5, 4][i % 3];
      lean = [6, 8, 7][i % 3];
      gripDx = [4, 6, 5][i % 3];
      fa = { x: 16, y: -6 };
      ra = { x: -20, y: -6 };
      sensor = 1;
      extra = 1;
      break;
    }
    case 'stagger': {
      hy += [2, 3][i % 2];
      hx -= 2;
      lean = [-5, -4][i % 2];
      tilt = [-0.18, -0.12][i % 2];
      gripDx = -3;
      sensor = [0.3, 0.55][i % 2];
      extra = 1;
      break;
    }
    default:
      break;
  }
  const hip = { x: hx, y: hy };
  return {
    hip,
    lean,
    head: { x: hx + 19 + lean, y: hy - 49 },
    headTilt: tilt,
    shoulder: { x: hx + 12 + lean, y: hy - 45 },
    grip: { x: hx + 34 + lean + gripDx, y: hy - 25 },
    legF: leg({ x: hx + 4, y: hy }, fa, L1, L2, -1, fFoot),
    legR: leg({ x: hx - 8, y: hy }, ra, L1, L2, -1, rFoot),
    sensor,
    extra,
  };
}

/** Insectoid drone. */
export function hornetPose(anim: EnemyAnim, i: number): HornetPose {
  const flap = [-2.9, -2.35, -1.75, -2.35];
  const k = i % 4;
  switch (anim) {
    case 'move':
      return { tilt: 0.2, wingF: flap[k] - 0.1, wingR: flap[k] + 0.12, sting: 0.15, stingAng: 0.55, curl: 0.1, bob: [0, -0.8, -1.2, -0.8][k], sensor: 0.85, blur: 1 };
    case 'windup':
      return { tilt: [-0.12, -0.24, -0.3][i % 3], wingF: -1.75, wingR: -1.6, sting: [0.4, 0.8, 1][i % 3], stingAng: [0.35, 0.18, 0.08][i % 3], curl: [0.4, 0.8, 1][i % 3], bob: 0, sensor: 1, blur: 0.6 };
    case 'attack':
      return { tilt: 0, wingF: [-3.0, -2.95][i % 2], wingR: [-2.92, -2.86][i % 2], sting: 1, stingAng: 0, curl: 0.2, bob: 0, sensor: 1, blur: 0.4 };
    case 'stagger':
      return { tilt: [0.45, 0.35][i % 2], wingF: [-2.5, -1.4][i % 2], wingR: [-1.2, -2.6][i % 2], sting: 0.1, stingAng: 0.8, curl: 0.3, bob: 0, sensor: [0.2, 0.5][i % 2], blur: 0.2 };
    case 'idle':
    default:
      return { tilt: 0.04, wingF: flap[k], wingR: flap[k] + 0.12, sting: 0.15, stingAng: 0.6, curl: 0.15, bob: [0, -0.8, -1.2, -0.8][k], sensor: [0.8, 0.9, 1, 0.9][k], blur: 1 };
  }
}

// ───────────────────────────── Frame metadata ─────────────────────────────

export interface FrameMeta {
  /** Gun / arm pivot (trooper, lancer) or shield grip (bulwark). */
  pivot: Vec2;
  /** Primary sensor (eye) — runtime glow + telegraph flare. */
  sensor: Vec2;
  /** Top of the head (markers). */
  top: number;
}

function hornetMeta(p: HornetPose): FrameMeta {
  // Sensor sits on the head, rotated with the body tilt.
  const ex = 19;
  const ey = 0 + p.bob;
  const c = Math.cos(p.tilt);
  const s = Math.sin(p.tilt);
  return { pivot: { x: 0, y: 0 }, sensor: { x: ex * c - ey * s, y: ex * s + ey * c }, top: -22 };
}

const metaCache = new Map<string, FrameMeta>();
const metaSource: Partial<Record<EnemyId, (anim: EnemyAnim, i: number) => FrameMeta>> = {};

/**
 * Replace a type's attachment points (painted art: the gun pivot / sensor of the painted parts
 * instead of the procedural pose's). Clears that type's cached frames.
 */
export function setFrameMetaSource(id: EnemyId, fn: ((anim: EnemyAnim, i: number) => FrameMeta) | null): void {
  if (fn) metaSource[id] = fn;
  else delete metaSource[id];
  for (const k of [...metaCache.keys()]) if (k.startsWith(`${id}:`)) metaCache.delete(k);
}

/** Attachment points of one frame (cached). */
export function frameMeta(id: EnemyId, anim: EnemyAnim, i: number): FrameMeta {
  const key = `${id}:${anim}:${i}`;
  let m = metaCache.get(key);
  if (m) return m;
  const src = metaSource[id];
  if (src) {
    m = src(anim, i);
    metaCache.set(key, m);
    return m;
  }
  switch (id) {
    case 'trooper': {
      const p = trooperPose(anim, i);
      m = { pivot: p.shoulder, sensor: { x: p.head.x + 6, y: p.head.y - 1 }, top: p.head.y - 8 };
      break;
    }
    case 'lancer': {
      const p = lancerPose(anim, i);
      m = { pivot: p.shoulder, sensor: { x: p.head.x + 3, y: p.head.y - 2 }, top: p.head.y - 10 };
      break;
    }
    case 'bulwark': {
      const p = bulwarkPose(anim, i);
      m = { pivot: p.grip, sensor: { x: p.head.x + 5, y: p.head.y }, top: p.head.y - 12 };
      break;
    }
    case 'hornet':
    default:
      m = hornetMeta(hornetPose(anim, i));
      break;
  }
  metaCache.set(key, m);
  return m;
}
